import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Users, Plus, Trash2, Edit3, Check, Crown, CreditCard, TrendingUp, Loader2, Star, X } from 'lucide-react';

const COLORS = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EAB308', '#EC4899'];
const CYCLES = [{ v: 'monthly', l: 'Mensual' }, { v: 'quarterly', l: 'Trimestral' }, { v: 'annual', l: 'Anual' }];

function PlanCard({ plan, onEdit, onDelete }) {
  return (
    <Card className="border-0 shadow-sm overflow-hidden relative group">
      <div className="h-1.5 w-full" style={{ backgroundColor: plan.color || '#F97316' }} />
      {plan.featured && (
        <div className="absolute top-4 right-4 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
          <Star className="h-2.5 w-2.5" /> Popular
        </div>
      )}
      <CardContent className="p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-1">{plan.name}</h3>
        <p className="text-xs text-slate-400 mb-4 min-h-[36px]">{plan.description || 'Sin descripción'}</p>
        <div className="mb-4">
          <span className="text-3xl font-black" style={{ color: plan.color || '#F97316' }}>${Number(plan.price).toLocaleString('es-MX')}</span>
          <span className="text-xs text-slate-400 font-semibold ml-1">MXN</span>
          <span className="text-slate-400 text-sm">/{CYCLES.find(c => c.v === plan.billing_cycle)?.l?.toLowerCase() || 'mes'}</span>
        </div>
        <div className="space-y-1.5 mb-5">
          {(plan.benefits || []).map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
              <Check className="h-3.5 w-3.5 flex-shrink-0" style={{ color: plan.color || '#F97316' }} />
              {b}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between text-xs text-slate-400 border-t pt-3">
          <span>{plan.subscribers || 0} suscriptores</span>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onEdit(plan)} className="p-1.5 hover:bg-slate-100 rounded-lg"><Edit3 className="h-3.5 w-3.5 text-slate-500" /></button>
            <button onClick={() => onDelete(plan.id)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanModal({ open, onClose, initial, onSave }) {
  const empty = { name: '', description: '', price: 29, billing_cycle: 'monthly', color: COLORS[0], benefits: [''], featured: false };
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial || empty); }, [open, initial]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nombre requerido'); return; }
    setSaving(true);
    try {
      if (form.id) await api.patch(`/memberships/plans/${form.id}`, form);
      else await api.post('/memberships/plans', form);
      toast.success(form.id ? 'Plan actualizado' : 'Plan creado');
      onSave(); onClose();
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const addBenefit = () => setForm(f => ({ ...f, benefits: [...(f.benefits || []), ''] }));
  const updateBenefit = (i, v) => setForm(f => ({ ...f, benefits: f.benefits.map((b, idx) => idx === i ? v : b) }));
  const removeBenefit = (i) => setForm(f => ({ ...f, benefits: f.benefits.filter((_, idx) => idx !== i) }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{form.id ? 'Editar plan' : 'Nuevo plan de membresía'}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Nombre del plan *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Plan Premium" className="text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Color</Label>
              <div className="flex gap-1.5 flex-wrap">
                {COLORS.map(c => (
                  <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                    className={cn("w-7 h-7 rounded-full border-2 transition-all", form.color === c ? "border-slate-700 scale-110" : "border-transparent")}
                    style={{ backgroundColor: c }} />
                ))}
                <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                  className="w-7 h-7 rounded-full border border-slate-200 cursor-pointer p-0.5" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Descripción</Label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Precio (MXN $)</Label>
              <Input type="number" min={0} step={0.01} value={form.price} onChange={e => setForm(f => ({ ...f, price: +e.target.value }))} className="text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Ciclo de facturación</Label>
              <Select value={form.billing_cycle} onValueChange={v => setForm(f => ({ ...f, billing_cycle: v }))}>
                <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CYCLES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-slate-500">Beneficios incluidos</Label>
              <button onClick={addBenefit} className="text-xs text-orange-600 hover:underline font-medium">+ Agregar</button>
            </div>
            <div className="space-y-2">
              {(form.benefits || []).map((b, i) => (
                <div key={i} className="flex gap-2">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-2" />
                  <Input value={b} onChange={e => updateBenefit(i, e.target.value)} placeholder="Ej: Reserva prioritaria" className="text-sm flex-1" />
                  <button onClick={() => removeBenefit(i)} className="p-1.5 text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
            <Crown className="h-4 w-4 text-amber-500" />
            <div className="flex-1">
              <Label className="text-xs font-medium text-amber-700">Marcar como plan popular</Label>
              <p className="text-[10px] text-amber-600">Se mostrará destacado en la página pública</p>
            </div>
            <input type="checkbox" checked={!!form.featured} onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))}
              className="rounded" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            {form.id ? 'Guardar cambios' : 'Crear plan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Memberships() {
  const [plans, setPlans] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('plans');
  const [showModal, setShowModal] = useState(false);
  const [editPlan, setEditPlan] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pR, sR] = await Promise.all([api.get('/memberships/plans'), api.get('/memberships/subscribers')]);
      setPlans(pR.data || []);
      setSubscribers(sR.data || []);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const deletePlan = async (id) => {
    if (!window.confirm('¿Eliminar este plan?')) return;
    try { await api.delete(`/memberships/plans/${id}`); fetchAll(); toast.success('Plan eliminado'); }
    catch { toast.error('Error'); }
  };

  const openEdit = (plan) => { setEditPlan(plan); setShowModal(true); };
  const openCreate = () => { setEditPlan(null); setShowModal(true); };

  const totalRevenue = subscribers.filter(s => s.status === 'active').reduce((sum, s) => {
    const plan = plans.find(p => p.id === s.plan_id);
    return sum + (plan?.price || 0);
  }, 0);

  if (loading) return (
    <div className="h-screen flex flex-col"><TopBar title="Membresías" helpSection="memberships" />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Membresías" helpSection="memberships" actions={
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
          <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo plan
        </Button>
      } />

      <div className="flex-1 p-6 max-w-6xl mx-auto w-full space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-purple-500 flex items-center justify-center">
            <Crown className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Membresías y Suscripciones</h1>
            <p className="text-sm text-slate-500">Planes de fidelidad con beneficios exclusivos para tus mejores clientes</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[
            { l: 'Planes activos', v: plans.length, icon: CreditCard, c: 'text-blue-600', bg: 'bg-blue-50' },
            { l: 'Suscriptores', v: subscribers.filter(s => s.status === 'active').length, icon: Users, c: 'text-green-600', bg: 'bg-green-50' },
            { l: 'Ingresos recurrentes', v: `$${totalRevenue.toLocaleString('es-MX', {maximumFractionDigits:0})} MXN/mes`, icon: TrendingUp, c: 'text-orange-600', bg: 'bg-orange-50' },
          ].map(s => (
            <Card key={s.l} className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center mb-3", s.bg)}>
                  <s.icon className={cn("h-5 w-5", s.c)} />
                </div>
                <div className="text-3xl font-black text-slate-800">{s.v}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.l}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          {[{ k: 'plans', l: 'Planes' }, { k: 'subscribers', l: 'Suscriptores' }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all",
                tab === t.k ? "bg-white shadow-sm text-slate-800" : "text-slate-500 hover:text-slate-700")}>
              {t.l}
            </button>
          ))}
        </div>

        {tab === 'plans' && (
          plans.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-16 text-center">
                <Crown className="h-12 w-12 mx-auto text-slate-200 mb-3" />
                <p className="text-slate-500 font-medium">Sin planes de membresía</p>
                <p className="text-sm text-slate-400 mt-1 mb-4">Crea tu primer plan para ofrecer beneficios exclusivos</p>
                <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="h-4 w-4 mr-1" /> Crear plan</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {plans.map(plan => (
                <PlanCard key={plan.id} plan={plan} onEdit={openEdit} onDelete={deletePlan} />
              ))}
              <button onClick={openCreate} className="border-2 border-dashed border-slate-200 rounded-2xl p-6 flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-orange-400 hover:text-orange-400 transition-all min-h-[200px]">
                <Plus className="h-8 w-8" /><span className="text-sm font-medium">Añadir plan</span>
              </button>
            </div>
          )
        )}

        {tab === 'subscribers' && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {subscribers.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Sin suscriptores aún</p>
                </div>
              ) : (
                <div className="divide-y">
                  {subscribers.map((s, i) => {
                    const plan = plans.find(p => p.id === s.plan_id);
                    return (
                      <div key={i} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-50">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-800">{s.name}</p>
                          <p className="text-xs text-slate-400">{s.email}</p>
                        </div>
                        <span className="text-sm text-slate-600">{plan?.name || '—'}</span>
                        <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold",
                          s.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600')}>
                          {s.status === 'active' ? 'Activo' : 'Inactivo'}
                        </span>
                        <span className="text-xs text-slate-400">{s.created_at ? new Date(s.created_at).toLocaleDateString('es-ES') : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <PlanModal open={showModal} onClose={() => setShowModal(false)} initial={editPlan} onSave={fetchAll} />
    </div>
  );
}
